import { Reference } from '@approvalmax/types';
import uniqBy from 'lodash/uniqBy';
import { backend, domain, State } from 'modules/data';
import { useDispatch, useSelector, useShallowEqualSelector } from 'modules/react-redux';
import { getCanCreateContact, getRequestEditMode } from 'pages/requestForm/selectors/requestSelectors';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from 'services/api';

import {
    changeQbooksDate,
    changeQbooksDepartment,
    changeQbooksExpenseNumber,
    changeQbooksNewCreatedVendor,
    changeQbooksPayee,
    changeQbooksPayeeType,
    changeQbooksPaymentAccount,
    changeQbooksPaymentMethod,
    changeQbooksPaymentType,
    showQBooksContactCreationPopup,
} from '../../../actions';
import { mapAccounts } from '../../../data/qbooks/QBooksContext';
import { parseQBooksCurrency } from '../../../data/qbooks/QBooksCurrency';
import { getRequest, getShowValidationErrors } from '../../../selectors/pageSelectors';
import {
    getIsRequiredLocation,
    getIsRequiredPayee,
    getIsRequiredPaymentMethod,
    getQBooksContext,
} from '../../../selectors/qbooks';
import { getRuleTypeByEditMode } from '../../../utils/helpers';
import { messages } from './QBooksExpenseFieldsSection.messages';

export const useCoreData = () => {
    return useShallowEqualSelector((state) => {
        const request = getRequest<domain.QBooksExpenseRequest>(state);
        const details = request.details;
        const context = getQBooksContext(state);
        const isShowErrors = getShowValidationErrors(state);

        return {
            request,
            details,
            context,
            isShowErrors,
        };
    });
};

export const usePayeeType = (paymentAccountCurrency?: Reference) => {
    const dispatch = useDispatch();

    const { request, details, context, isShowErrors } = useCoreData();

    const payeeType = useMemo(() => details.payeeType, [details.payeeType]);
    const payeeTypeItems = useMemo(() => {
        let payeeTypes = context.payeeTypes || [];

        if (paymentAccountCurrency && paymentAccountCurrency.id !== request.companyCurrency) {
            payeeTypes = payeeTypes.filter(
                (payeeType) => payeeType.id !== backend.IntegrationsQBooksPayeeTypes.Employee
            );
        }

        return payeeTypes;
    }, [context.payeeTypes, paymentAccountCurrency, request.companyCurrency]);
    const payeeTypePlaceholder = messages.payeeTypePlaceholder;
    const isDisabledPayeeType = useMemo(() => !payeeTypeItems.length, [payeeTypeItems.length]);
    const isRequiredPayeeType = useMemo(() => getIsRequiredPayee(request).payeeType, [request]);
    const isInvalidPayeeType = useMemo(
        () => isShowErrors && isRequiredPayeeType && !payeeType?.id,
        [isShowErrors, isRequiredPayeeType, payeeType?.id]
    );

    const onChangePayeeType = useCallback(
        (value: domain.QBooksPayeeType) => {
            dispatch(changeQbooksPayeeType(value));
            dispatch(changeQbooksPayee(null));
        },
        [dispatch]
    );

    return {
        payeeType,
        payeeTypeItems,
        payeeTypePlaceholder,
        isDisabledPayeeType,
        isRequiredPayeeType,
        isInvalidPayeeType,
        onChangePayeeType,
    };
};

export const usePayee = (
    onPaymentAccountChange: (value: domain.QBooksAccount | null) => void,
    paymentAccountCurrency?: Reference
) => {
    const dispatch = useDispatch();

    const { request, details, isShowErrors, context } = useCoreData();

    const [payeeCurrency, setPayeeCurrency] = useState<Reference | undefined>(context.payee?.currency);

    const payee = useMemo(() => details.payee, [details.payee]);
    const payeeType = useMemo(() => details.payeeType, [details.payeeType]);
    const paymentAccount = useMemo(() => details.paymentAccount, [details.paymentAccount]);
    const payeePlaceholder = messages.payeePlaceholder;
    const isDisabledPayee = useMemo(() => !payeeType?.id, [payeeType]);
    const isRequiredPayee = useMemo(() => getIsRequiredPayee(request).payeeId, [request]);
    const isInvalidPayee = useMemo(
        () => isShowErrors && isRequiredPayee && !payee?.id,
        [isShowErrors, isRequiredPayee, payee?.id]
    );
    const canCreateContact = useSelector((state) => getCanCreateContact(state, request));

    useEffect(() => {
        if (!payee) {
            setPayeeCurrency(undefined);
        } else if (payee?.currency) {
            setPayeeCurrency(payee?.currency);
        }
    }, [payee]);

    useEffect(() => {
        if (details.newCreatedVendor) {
            // set new created vendor from QBooksCounterpartyCreationPopup as selected payee
            dispatch(changeQbooksPayee(details.newCreatedVendor));

            // clear new created vendor, otherwise we will get infinity loop
            dispatch(changeQbooksNewCreatedVendor(null));

            if (
                paymentAccountCurrency &&
                paymentAccount &&
                paymentAccountCurrency.id !== request.companyCurrency &&
                details.newCreatedVendor.currency?.id !== paymentAccountCurrency.id
            ) {
                // clear payment account if payee has other currency
                onPaymentAccountChange(null);
            }
        }
    }, [
        details.newCreatedVendor,
        dispatch,
        onPaymentAccountChange,
        paymentAccount,
        paymentAccountCurrency,
        request.companyCurrency,
        request.currency,
    ]);

    const canCreateNewVendorFn = useMemo(
        () => () => canCreateContact && payeeType?.id === backend.IntegrationsQBooksPayeeTypes.Vendor,
        [payeeType?.id, canCreateContact]
    );
    const createNewVendorTextFn = useCallback(() => messages.addNewVendor, []);
    const onCreateVendor = useCallback(() => {
        dispatch(showQBooksContactCreationPopup());

        return Promise.resolve(null);
    }, [dispatch]);

    const onChangePayee = useCallback(
        (value: domain.QBooksContact | null) => {
            dispatch(changeQbooksPayee(value));
        },
        [dispatch]
    );

    return {
        payee,
        payeePlaceholder,
        isDisabledPayee,
        isRequiredPayee,
        isInvalidPayee,
        canCreateNewVendorFn,
        createNewVendorTextFn,
        onCreateVendor,
        onChangePayee,
        payeeCurrency,
    };
};

export const usePayeeItems = (paymentAccountCurrency?: string) => {
    const { request, details, context } = useCoreData();

    const editMode = useSelector((state) => getRequestEditMode(state, request));
    const ruleType = getRuleTypeByEditMode(editMode);
    const payeeType = useMemo(() => details.payeeType, [details.payeeType]);

    const [payees, setPayees] = useState<domain.QBooksContact[]>([]);
    const [cachedPayees, setCachedPayees] = useState<{ [key: string]: domain.QBooksContact[] }>({});
    const [loadingPayees, setLoadingPayees] = useState(false);

    const preparePayees = useCallback(
        (responseItems: backend.IntegrationsQBOCounterparty[] | backend.IntegrationsQBooksEmployee[]) =>
            (responseItems || []).map((item) => {
                const { Id, Name } = item;
                const currency = 'Currency' in item && item.Currency ? parseQBooksCurrency(item.Currency) : undefined;

                const taxId = 'TaxId' in item && item.TaxId ? item.TaxId : undefined;

                let address = 'PrimaryAddr' in item && item.PrimaryAddr ? [item.PrimaryAddr] : undefined;

                if ('Addresses' in item && item.Addresses?.length) {
                    const [targetAddress] = item.Addresses;

                    address = [
                        [
                            targetAddress.AddressLine1,
                            targetAddress.AddressLine2,
                            targetAddress.AddressLine3,
                            targetAddress.AddressLine4,
                        ]
                            .filter(Boolean)
                            .join(', '),
                        [targetAddress.City, targetAddress.Region, targetAddress.PostalCode, targetAddress.Country]
                            .filter(Boolean)
                            .join(', '),
                    ].filter(Boolean);
                }

                let email;

                switch (true) {
                    case 'Email' in item && Boolean(item.Email):
                        email = item.Email;
                        break;

                    case 'ContactPersons' in item && Boolean(item.ContactPersons.length):
                        email = item.ContactPersons[0].EmailAddress;
                        break;

                    default:
                        email = undefined;
                }

                return {
                    id: Id,
                    text: `${Name}${context.isMultiCurrencyEnabled && currency ? ` (${currency.id})` : ''}`,
                    currency,
                    taxId,
                    address,
                    email,
                };
            }),
        [context.isMultiCurrencyEnabled]
    );

    const loadPayees = useCallback(
        async (payeeTypeId: keyof typeof backend.IntegrationsQBooksPayeeTypes) => {
            const getTransferCurrencyCode = (currencyCode?: string) => {
                return payeeTypeId === backend.IntegrationsQBooksPayeeTypes.Employee ||
                    request.companyCurrency === currencyCode
                    ? undefined
                    : currencyCode;
            };

            const getCacheStorageId = (transferCurrency?: string) => `${payeeTypeId}.${transferCurrency || ''}`;

            const transferCurrencyCode = getTransferCurrencyCode(paymentAccountCurrency);

            const cacheStorageId = getCacheStorageId(transferCurrencyCode);

            if (details.newCreatedVendor?.currency?.id) {
                // clear cache for keys where we should get new created vendor
                setCachedPayees((prevCachedPayees) => {
                    const newCachedPayees = { ...prevCachedPayees };
                    const newCreatedVendorTransferCurrencyCode = getTransferCurrencyCode(
                        details.newCreatedVendor?.currency?.id
                    );
                    const baseRequestTransferCurrencyCode = getTransferCurrencyCode(request.companyCurrency);

                    delete newCachedPayees[getCacheStorageId(newCreatedVendorTransferCurrencyCode)];
                    delete newCachedPayees[getCacheStorageId(baseRequestTransferCurrencyCode)];

                    return newCachedPayees;
                });
            } else if (cachedPayees[cacheStorageId]) {
                return cachedPayees[cacheStorageId];
            }

            setLoadingPayees(true);

            let response;
            let responseItems: backend.IntegrationsQBOCounterparty[] | backend.IntegrationsQBooksEmployee[] = [];

            const transfer = {
                requestId: request.id,
                currencyCode: transferCurrencyCode,
                companyId: request.companyId,
                ruleType,
            };

            switch (payeeTypeId) {
                case backend.IntegrationsQBooksPayeeTypes.Vendor:
                    response = await api.integration.getQBooksPayeeVendors(transfer);
                    responseItems = response.Contacts;
                    break;

                case backend.IntegrationsQBooksPayeeTypes.Customer:
                    response = await api.integration.getQBooksPayeeCustomers(transfer);
                    responseItems = response.Contacts;
                    break;

                case backend.IntegrationsQBooksPayeeTypes.Employee:
                    response = await api.integration.getQBooksPayeeEmployees(transfer);
                    responseItems = response.Employees;
                    break;
            }

            const items = preparePayees(responseItems);

            setCachedPayees((prevCachedPayees) => ({
                ...prevCachedPayees,
                [cacheStorageId]: items,
            }));
            setLoadingPayees(false);

            return items;
        },
        [
            cachedPayees,
            details.newCreatedVendor?.currency?.id,
            paymentAccountCurrency,
            preparePayees,
            request.companyCurrency,
            request.id,
            request.companyId,
            ruleType,
        ]
    );

    useEffect(() => {
        const fetchNewPayees = async (payeeTypeId: keyof typeof backend.IntegrationsQBooksPayeeTypes) => {
            const newPayees = await loadPayees(payeeTypeId);

            setPayees(newPayees);
        };

        if (payeeType?.id) {
            fetchNewPayees(payeeType.id as backend.IntegrationsQBooksPayeeTypes);
        }
    }, [loadPayees, paymentAccountCurrency, payeeType?.id]);

    return { payees: loadingPayees ? [] : payees, loadingPayees };
};

export const usePaymentAccount = () => {
    const dispatch = useDispatch();

    const { details, isShowErrors, context, request } = useCoreData();

    const [paymentAccountCurrency, setPaymentAccountCurrency] = useState<Reference | undefined>(
        context.paymentAccount?.currency
    );
    const [paymentAccountType, setPaymentAccountType] = useState<keyof typeof domain.QBooksAccountType | undefined>(
        context.paymentAccount?.type
    );

    const paymentAccount = useMemo(() => details.paymentAccount, [details.paymentAccount]);
    const paymentAccountPlaceholder = useMemo(() => messages.paymentAccountPlaceholder, []);
    const isInvalidPaymentAccount = useMemo(() => isShowErrors && !paymentAccount, [isShowErrors, paymentAccount]);

    const onPaymentAccountChange = useCallback(
        (value: domain.QBooksAccount | null) => {
            dispatch(changeQbooksPaymentAccount(value));
            setPaymentAccountCurrency(value?.currency);
            setPaymentAccountType(value?.type);

            if (
                value &&
                value.currency?.id !== request.companyCurrency &&
                details.payeeType?.id === backend.IntegrationsQBooksPayeeTypes.Employee
            ) {
                dispatch(changeQbooksPayeeType(null));
                dispatch(changeQbooksPayee(null));
            }
        },
        [details.payeeType?.id, dispatch, request.companyCurrency]
    );

    return {
        paymentAccount,
        paymentAccountPlaceholder,
        isInvalidPaymentAccount,
        onPaymentAccountChange,
        paymentAccountCurrency,
        paymentAccountType,
    };
};

export const usePaymentAccountItems = (payeeCurrency?: string) => {
    const { request, context, details } = useCoreData();

    const editMode = useSelector((state) => getRequestEditMode(state, request));
    const ruleType = getRuleTypeByEditMode(editMode);
    const [cachedAccounts, setCachedAccounts] = useState<{ [key: string]: domain.QBooksAccount[] }>({});
    const [accountsWithBaseCurrency, setAccountsWithBaseCurrency] = useState<domain.QBooksAccount[]>([]);
    const [accountsWithPayeeCurrency, setAccountsWithPayeeCurrency] = useState<domain.QBooksAccount[]>([]);
    const [loadingPaymentAccounts, setLoadingPaymentAccounts] = useState(false);

    const loadAccounts = useCallback(
        async (currencyCode?: string) => {
            const cacheStorageId = currencyCode || '';

            if (cachedAccounts[cacheStorageId]) {
                return cachedAccounts[cacheStorageId];
            }

            setLoadingPaymentAccounts(true);

            const response = await api.integration.getQBooksPaymentAccounts({
                requestId: request.id,
                currencyCode,
                companyId: request.companyId,
                ruleType,
            });

            const newAccounts = mapAccounts(response.Accounts, context.isMultiCurrencyEnabled).map(
                (account: domain.QBooksAccount) => {
                    const normalizedType = account.type ? domain.QBooksAccountTypeNormalized[account.type] : '';

                    return {
                        ...account,
                        normalizedType,
                    };
                }
            );

            setCachedAccounts((prevCachedAccounts) => ({
                ...prevCachedAccounts,
                [cacheStorageId]: newAccounts,
            }));
            setLoadingPaymentAccounts(false);

            return newAccounts;
        },
        [cachedAccounts, context.isMultiCurrencyEnabled, request.id, request.companyId, ruleType]
    );

    useEffect(() => {
        const fetchAccountsWithBaseCurrency = async () => {
            const newAccountsWithBaseCurrency = await loadAccounts(request.companyCurrency);

            setAccountsWithBaseCurrency(newAccountsWithBaseCurrency);
        };

        fetchAccountsWithBaseCurrency();
    }, [loadAccounts, request.companyCurrency]);

    useEffect(() => {
        const fetchItemsWithPayeeCurrency = async () => {
            const transferCurrency =
                details.payeeType?.id === backend.IntegrationsQBooksPayeeTypes.Employee
                    ? request.companyCurrency
                    : payeeCurrency;
            const newAccountsWithPayeeCurrency = await loadAccounts(transferCurrency);

            setAccountsWithPayeeCurrency(newAccountsWithPayeeCurrency);
        };

        fetchItemsWithPayeeCurrency();
    }, [payeeCurrency, loadAccounts, request.companyCurrency, details.payeeType?.id]);

    const paymentAccounts = useMemo(() => {
        const newAccounts = [...accountsWithBaseCurrency, ...accountsWithPayeeCurrency];

        let uniqNewAccounts = uniqBy(newAccounts, (paymentAccount) => paymentAccount.id);

        if (details.paymentType?.id === backend.IntegrationQBooksPaymentTypeId.Check) {
            uniqNewAccounts = uniqNewAccounts.filter(
                (account) =>
                    account.type !== domain.QBooksAccountType.CreditCard &&
                    account.type !== domain.QBooksAccountType.OtherCurrentAsset
            );
        }

        return uniqNewAccounts;
    }, [accountsWithBaseCurrency, accountsWithPayeeCurrency, details.paymentType]);

    return {
        paymentAccounts: loadingPaymentAccounts ? [] : paymentAccounts,
        loadingPaymentAccounts,
    };
};

export const usePaymentMethod = () => {
    const dispatch = useDispatch();

    const { request, details, context, isShowErrors } = useCoreData();

    const isRequiredPaymentMethod = useSelector((state: State) => getIsRequiredPaymentMethod(state, request));
    const paymentMethod = useMemo(() => details.paymentMethod, [details.paymentMethod]);
    const paymentMethodItems = useMemo(() => context.paymentMethods || [], [context.paymentMethods]);
    const paymentMethodPlaceholder = messages.paymentMethodPlaceholder;
    const isInvalidPaymentMethod = useMemo(
        () => isRequiredPaymentMethod && isShowErrors && !paymentMethod?.id,
        [isShowErrors, paymentMethod, isRequiredPaymentMethod]
    );

    const onChangePaymentMethod = useCallback(
        (value: domain.QBooksPaymentMethod) => dispatch(changeQbooksPaymentMethod(value)),
        [dispatch]
    );

    return {
        paymentMethod,
        paymentMethodItems,
        paymentMethodPlaceholder,
        onChangePaymentMethod,
        isRequiredPaymentMethod,
        isInvalidPaymentMethod,
    };
};

export const usePaymentType = (paymentAccountType?: keyof typeof domain.QBooksAccountType) => {
    const dispatch = useDispatch();

    const { details, context, isShowErrors } = useCoreData();

    const paymentType = useMemo(() => details.paymentType, [details.paymentType]);
    const paymentTypeItems = useMemo(() => {
        let paymentTypes = context.paymentTypes || [];

        if (
            paymentAccountType === domain.QBooksAccountType.CreditCard ||
            paymentAccountType === domain.QBooksAccountType.OtherCurrentAsset
        ) {
            paymentTypes = paymentTypes.filter(
                (paymentType) => paymentType.id !== backend.IntegrationQBooksPaymentTypeId.Check
            );
        }

        return paymentTypes;
    }, [context.paymentTypes, paymentAccountType]);
    const paymentTypePlaceholder = messages.paymentTypePlaceholder;
    const isInvalidPaymentType = useMemo(() => isShowErrors && !paymentType, [isShowErrors, paymentType]);

    const onChangePaymentType = useCallback(
        (value: domain.QBooksPayeeType) => dispatch(changeQbooksPaymentType(value)),
        [dispatch]
    );

    return {
        paymentType,
        paymentTypeItems,
        paymentTypePlaceholder,
        isInvalidPaymentType,
        onChangePaymentType,
    };
};

export const usePaymentDate = () => {
    const dispatch = useDispatch();

    const { details, isShowErrors } = useCoreData();

    const paymentDate = useMemo(() => details.date, [details.date]);
    const paymentDatePlaceholder = messages.paymentDatePlaceholder;
    const isInvalidPaymentDate = useMemo(() => isShowErrors && !paymentDate, [isShowErrors, paymentDate]);

    const onChangePaymentDate = useCallback((value: string) => dispatch(changeQbooksDate(value!)), [dispatch]);

    return {
        paymentDate,
        paymentDatePlaceholder,
        isInvalidPaymentDate,
        onChangePaymentDate,
    };
};

export const useRefNo = () => {
    const dispatch = useDispatch();

    const { details } = useCoreData();

    const refNo = useMemo(() => details.expenseNumber || '', [details.expenseNumber]);
    const refNoPlaceholder = messages.refNoPlaceholder;

    const onChangeRefNo = useCallback((value: string) => dispatch(changeQbooksExpenseNumber(value)), [dispatch]);

    return {
        refNo,
        refNoPlaceholder,
        onChangeRefNo,
    };
};

export const useDepartment = () => {
    const dispatch = useDispatch();

    const { request, details, context, isShowErrors } = useCoreData();

    const isRequiredDepartment = useSelector((state: State) => getIsRequiredLocation(state, request));
    const department = useMemo(() => details.department, [details.department]);
    const departmentItems = useMemo(() => context.departments, [context.departments]);
    const isShowDepartment = useMemo(() => context.hasDepartmentFeature, [context.hasDepartmentFeature]);
    const departmentLabel = context.departmentField?.text || messages.locationField;
    const departmentPlaceholder = messages.departmentPlaceholder({ department: departmentLabel });
    const isInvalidDepartment = useMemo(
        () => isRequiredDepartment && isShowDepartment && isShowErrors && !department,
        [isRequiredDepartment, isShowDepartment, isShowErrors, department]
    );

    const onChangeDepartment = useCallback((value: Reference) => dispatch(changeQbooksDepartment(value)), [dispatch]);

    return {
        department,
        departmentItems,
        isShowDepartment,
        departmentLabel,
        departmentPlaceholder,
        onChangeDepartment,
        isRequiredDepartment,
        isInvalidDepartment,
    };
};
